/**
 * La guía de referencia de un flujo en un panel lateral, para leerla sin salir
 * de la corrida o del evento que se está mirando.
 *
 * Se arma con tres piezas que comparten un mismo estado:
 *
 *   const reference = useProcessReferencePanel(flowsOfRun(graph));
 *   <ProcessReferenceToggle state={reference}>Referencia del flujo</ProcessReferenceToggle>
 *   <ProcessReferencePanel state={reference} />
 *
 * Una corrida puede cruzar flujos (una carga de catálogo es de F12 y de F13):
 * el botón abre la guía del primero y el panel deja pasar a la de los otros.
 *
 * El panel puede ir en cualquier lugar de la pantalla: se dibuja en un portal y
 * la página le reserva su ancho sola en pantallas anchas (ver `SidePanel`).
 */
import { useCallback, useId, useMemo, useRef } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { findProcessReference } from '../lib/process-reference';
import type { ProcessReference } from '../lib/process-reference';
import { IconBook } from './icons';
import { ProcessReferenceContent } from './ProcessReferenceContent';
import { SidePanel } from './SidePanel';
import '../styles/process-reference.css';

/**
 * Parámetro de la query string que abre el panel. `?referencia=1` abre la guía
 * del primer flujo; `?referencia=F13`, la de ese flujo si la corrida pasa por él.
 */
export const PROCESS_REFERENCE_PARAM = 'referencia';

/** Valor del parámetro que abre la guía del primer flujo. */
const OPEN_FIRST = '1';

export interface ProcessReferencePanelState {
  /** Las guías de los flujos pedidos, sin repetir y en su orden. */
  readonly references: readonly ProcessReference[];
  /** La que abre el botón: la del primer flujo. `null` si no hay guía (no se ofrece el acceso). */
  readonly primary: ProcessReference | null;
  /** La que muestra el panel: la que pide la URL o, si no, la primera. */
  readonly reference: ProcessReference | null;
  /** Abierto: la URL lo pide y hay una guía que mostrar. */
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  /** Muestra la guía de otro de los flujos, con el panel abierto. */
  readonly select: (flow: string) => void;
  readonly toggle: () => void;
  /** El botón que lo abre: el foco vuelve ahí al cerrar. */
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  /** Id del panel, para el `aria-controls` del botón. */
  readonly panelId: string;
}

/**
 * Estado del panel de referencia para los flujos de una pantalla, el primero
 * como principal (el de la corrida, o el del evento que se está mirando).
 *
 * VIVE EN LA URL (`?referencia=…`) y no en un estado local: recargar, volver
 * atrás desde un evento o pegar el enlace en un canal de soporte deja la
 * pantalla igual, con el panel abierto en la misma guía. Se escribe con
 * `replace` porque abrir o cerrar el panel no es navegar: "atrás" tiene que
 * seguir llevando a la pantalla anterior, no a esta misma sin panel. Los demás
 * parámetros de la URL se conservan.
 */
export function useProcessReferencePanel(
  flows: readonly (string | null | undefined)[],
): ProcessReferencePanelState {
  const [searchParams, setSearchParams] = useSearchParams();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  // Quien llama arma la lista en cada render: manda su contenido, no su identidad.
  const flowsKey = flows.join('\n');
  const references = useMemo(() => {
    const found = new Map<string, ProcessReference>();
    for (const flow of flowsKey.split('\n')) {
      const reference = findProcessReference(flow);
      if (reference !== null) {
        found.set(reference.id, reference);
      }
    }
    return [...found.values()];
  }, [flowsKey]);

  const requested = searchParams.get(PROCESS_REFERENCE_PARAM);
  const primary = references[0] ?? null;
  const open = primary !== null && requested !== null;
  const reference = references.find((candidate) => candidate.id === requested) ?? primary;

  const writeParam = useCallback(
    (value: string | null) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (value === null) {
            params.delete(PROCESS_REFERENCE_PARAM);
          } else {
            params.set(PROCESS_REFERENCE_PARAM, value);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const setOpen = useCallback(
    (next: boolean) => writeParam(next ? OPEN_FIRST : null),
    [writeParam],
  );
  const select = useCallback((flow: string) => writeParam(flow), [writeParam]);
  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  return { references, primary, reference, open, setOpen, select, toggle, triggerRef, panelId };
}

export interface ProcessReferenceToggleProps {
  readonly state: ProcessReferencePanelState;
  /** `button`: un botón chico; `link`: se lee como un enlace dentro de un texto. */
  readonly variant?: 'button' | 'link' | undefined;
  /** Clases extra, por ejemplo `process-reference-toggle--end` para ir al final de la fila. */
  readonly className?: string | undefined;
  /** El texto del botón. Es su nombre accesible (el ícono no se anuncia). */
  readonly children: ReactNode;
}

/** Abre y cierra el panel. Sin guía para ningún flujo, no se muestra. */
export function ProcessReferenceToggle({
  state,
  variant = 'button',
  className,
  children,
}: ProcessReferenceToggleProps): ReactElement | null {
  if (state.primary === null) {
    return null;
  }
  const classes = [
    variant === 'button' ? 'button button--sm' : null,
    'process-reference-toggle',
    variant === 'link' ? 'process-reference-toggle--link' : null,
    className ?? null,
  ]
    .filter((value) => value !== null)
    .join(' ');

  return (
    <button
      ref={state.triggerRef}
      type="button"
      className={classes}
      aria-expanded={state.open}
      aria-controls={state.open ? state.panelId : undefined}
      onClick={state.toggle}
    >
      <IconBook size={16} className="process-reference-icon" />
      {children}
    </button>
  );
}

/** El panel con la guía compacta del flujo y, al pie, adónde seguir. */
export function ProcessReferencePanel({
  state,
}: {
  readonly state: ProcessReferencePanelState;
}): ReactElement | null {
  const { references, reference, open, setOpen, select, triggerRef, panelId } = state;
  if (!open || reference === null) {
    return null;
  }
  return (
    <SidePanel
      id={panelId}
      title={`${reference.id} · ${reference.title}`}
      subtitle="Guía de referencia"
      onClose={() => setOpen(false)}
      returnFocusRef={triggerRef}
      footer={
        <>
          <Link className="button button--sm" to={`/procesos?flujo=${reference.id}`}>
            Abrir en Procesos
          </Link>
          <Link className="button button--sm" to={`/mensajes?flow=${reference.id}`}>
            Ver mensajes de {reference.id}
          </Link>
        </>
      }
    >
      {references.length < 2 ? null : (
        <div className="process-reference__flows">
          <span className="process-reference__flows-intro">
            Esta corrida pasa por {references.length} flujos:
          </span>
          <div
            className="process-reference__flows-options"
            role="group"
            aria-label="Flujos de esta corrida"
          >
            {references.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                className="button button--sm process-reference__flow"
                aria-pressed={candidate.id === reference.id}
                title={`${candidate.id} · ${candidate.title}`}
                onClick={() => select(candidate.id)}
              >
                {candidate.id}
              </button>
            ))}
          </div>
        </div>
      )}
      <ProcessReferenceContent process={reference} variant="compact" />
    </SidePanel>
  );
}
